import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import { useQuery } from '@tanstack/react-query';
import { transactionApi } from '@/api/payments/transactionApi';
import { type TransactionDetail } from '@/models/payments/Transaction';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { formatDateTime, formatMoney } from './format';
import CardBrandChip from './CardBrandChip';

interface Props {
    open: boolean;
    transactionId: string | null;
    locationId: string;
    organizationId?: string;
    onClose: () => void;
}

// One label/value row; skips itself when the value is empty so blank fields don't clutter the panel.
const Field = ({ label, value }: { label: string; value?: string | number | null }) => {
    if (value === null || value === undefined || value === '') return null;
    return (
        <Box sx={{ display: 'flex', gap: 1.5, py: 0.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold', minWidth: 140, flexShrink: 0 }}>{label}</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
        </Box>
    );
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <Typography
        variant="subtitle2"
        sx={{
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            px: 1,
            py: 0.5,
            mb: 1,
            borderRadius: 0.5,
            bgcolor: (theme) => (theme.palette.mode === 'light' ? '#eef2f6' : theme.palette.action.hover),
            color: (theme) => (theme.palette.mode === 'light' ? '#334e68' : theme.palette.text.primary),
        }}
    >
        {children}
    </Typography>
);

// A section header plus its fields laid out in two columns.
const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <Box sx={{ mt: 2 }}>
        <SectionLabel>{title}</SectionLabel>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3 }}>{children}</Box>
    </Box>
);

const TransactionDetailDialog = ({ open, transactionId, locationId, organizationId, onClose }: Props) => {
    const strings = useLocalizedStrings();

    const { data: detail, isFetching: detailLoading } = useQuery<TransactionDetail>({
        queryKey: ['transaction', transactionId, organizationId],
        enabled: open && !!transactionId,
        queryFn: () => transactionApi.get(locationId, transactionId!, organizationId),
        staleTime: 60_000,
        refetchOnWindowFocus: false,
    });

    const { data: receipt, isFetching: receiptLoading } = useQuery({
        queryKey: ['transaction-receipt', transactionId, organizationId],
        enabled: open && !!transactionId,
        queryFn: () => transactionApi.getReceipt(locationId, transactionId!, organizationId),
        staleTime: 60_000,
        refetchOnWindowFocus: false,
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>{strings.transactionDetails}</DialogTitle>
            <DialogContent dividers sx={{ bgcolor: 'background.default' }}>
                {detailLoading && !detail ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                ) : !detail ? (
                    <Typography variant="body2" color="text.secondary">{strings.transactionNotFound}</Typography>
                ) : (
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                        {/* Left 60% — fields (two columns per section) + signature */}
                        <Box sx={{ flex: '0 0 60%', maxWidth: '60%' }}>
                            <Section title={strings.details}>
                                <Field label={strings.brand} value={detail.brandName} />
                                <Field label={strings.location} value={detail.locationName} />
                                <Field label={strings.employee} value={detail.employeeName} />
                                <Field label={strings.transactionType} value={detail.isVoided ? `${detail.transactionType}, ${strings.void}` : detail.transactionType} />
                                <Field label={strings.result} value={detail.transactionResult} />
                                <Field label={strings.date} value={formatDateTime(detail.transactionDateTimeLocal, detail.locale)} />
                                <Field label={strings.invoiceNumber} value={detail.invoiceNumber} />
                                <Field label={strings.description} value={detail.description} />
                            </Section>

                            <Section title={strings.amounts}>
                                <Field label={strings.total} value={formatMoney(detail.amount, detail.locale)} />
                                <Field label={strings.tax} value={formatMoney(detail.tax, detail.locale)} />
                                <Field label={strings.tip} value={formatMoney(detail.tip, detail.locale)} />
                            </Section>

                            <Section title={strings.payment}>
                                <Box sx={{ display: 'flex', gap: 1.5, py: 0.5, alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold', minWidth: 140, flexShrink: 0 }}>{strings.cardType}</Typography>
                                    <CardBrandChip issuer={detail.cardIssuer} />
                                </Box>
                                <Field label={strings.cardNumber} value={detail.cardNumber} />
                                <Field label={strings.paymentType} value={detail.paymentType} />
                                <Field label={strings.digitalWallet} value={detail.digitalWalletType} />
                                <Field label={strings.approvalCode} value={[detail.approvalCode, detail.isVoided ? detail.voidApprovalCode : null].filter(Boolean).join(', ')} />
                                <Field label={strings.responseCode} value={detail.responseCode} />
                                <Field label={strings.registerNumber} value={detail.registerNumber} />
                                <Field label={strings.posTransactionId} value={detail.posTransactionId} />
                                <Field label={strings.gatewayTransactionId} value={detail.paymentGatewayTransactionId} />
                            </Section>

                            <Box sx={{ mt: 2 }}>
                                <SectionLabel>{strings.signature}</SectionLabel>
                                {detail.signatureImageBase64 ? (
                                    <Box
                                        component="img"
                                        alt={strings.signature}
                                        src={`data:image/png;base64,${detail.signatureImageBase64}`}
                                        sx={{ maxWidth: '100%', border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }}
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary">{strings.noSignature}</Typography>
                                )}
                            </Box>
                        </Box>

                        <Divider orientation="vertical" flexItem />

                        {/* Right 40% — receipt. The column itself scrolls when the receipt is long. */}
                        <Box sx={{ flex: '1 1 40%', minWidth: 0, maxHeight: '65vh', overflowY: 'auto' }}>
                            <SectionLabel>{strings.receipt}</SectionLabel>
                            {receiptLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                            ) : receipt?.found ? (
                                <Box
                                    sx={{
                                        '& img': { maxWidth: '100%' },
                                        fontSize: 12,
                                        overflowX: 'auto',
                                        p: 1.5,
                                        // Gap so the column's scrollbar doesn't butt up against the receipt.
                                        mr: 1.5,
                                        bgcolor: '#fff',
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: 3,
                                    }}
                                    // Receipt HTML is rendered by our own Receipt service — trusted internal content.
                                    dangerouslySetInnerHTML={{ __html: receipt.html }}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary">{strings.noReceipt}</Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{strings.close}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default TransactionDetailDialog;
